import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface BaseKind {
  id?: string | number;
  name?: string;
}

interface IKindsFooter<T> {
  items: T[];
  isEverythingSelected: boolean;
  toggleAll: () => void;
  toggle: (item: T, checked: boolean) => void;
  isSelected: (item: T) => boolean;
  colorAccessor: string;
  applyColor: boolean;
  title: string;
  showPlanningAppointments?: boolean;
  planningAppointments: boolean;
  onPlanningAppointmentsChange: (value: boolean) => void;
  showSerialAppointments?: boolean;
  serialAppointments?: boolean;
  onSerialAppointmentsChange?: (value: boolean) => void;
}

export const KindsFooter = <T extends BaseKind>({
  items,
  isEverythingSelected,
  toggle,
  toggleAll,
  isSelected,
  colorAccessor,
  applyColor,
  title,
  showPlanningAppointments = true,
  planningAppointments,
  onPlanningAppointmentsChange,
  showSerialAppointments = true,
  serialAppointments = true,
  onSerialAppointmentsChange,
}: IKindsFooter<T>) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        gridArea: 'footer',
        display: 'flex',
        paddingInline: '1rem',
        gap: '1rem',
        alignItems: 'center',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        background: 'var(--ui02)',
        borderTop: '1px solid var(--ui04)',
        zIndex: '100',
      }}
    >
      {showPlanningAppointments && (
        <FormControlLabel
          label={t('COMMON.HIDE', {
            name: t('COMMON.PLANNINGAPPOINTMENT', { count: 2 }),
          })}
          control={
            <Checkbox
              checked={!planningAppointments}
              onChange={(_event) => {
                onPlanningAppointmentsChange(!planningAppointments);
              }}
            />
          }
        />
      )}
      {showSerialAppointments && (
        <>
          <FormControlLabel
            label={t('COMMON.HIDE', {
              name: t('COMMON.RECURRING_APPOINTMENT', { count: 2 }),
            })}
            control={
              <Checkbox
                checked={!serialAppointments}
                onChange={(_event) => {
                  onSerialAppointmentsChange(!serialAppointments);
                }}
              />
            }
          />
          <Divider
            orientation="vertical"
            flexItem
            sx={(theme) => ({ borderColor: theme.vars.palette.ui4.main })}
          />
        </>
      )}

      <Typography>{title}:</Typography>

      <Button variant="text" onClick={() => toggleAll()} sx={{ flex: 'none' }}>
        {isEverythingSelected
          ? t('COMMON.SELECT_NONE')
          : t('COMMON.SELECT_ALL')}
      </Button>

      <Divider
        orientation="vertical"
        flexItem
        sx={(theme) => ({ borderColor: theme.vars.palette.ui4.main })}
      />
      <Box
        sx={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
        }}
      >
        {items.map((item) => {
          const color = item[colorAccessor];

          return (
            <FormControlLabel
              key={item.id}
              label={item.name}
              control={
                <Checkbox
                  checked={isSelected(item)}
                  onChange={(_event, checked) => {
                    toggle(item, checked);
                  }}
                  sx={
                    applyColor
                      ? {
                          color,
                          '&.Mui-checked': {
                            color,
                          },
                        }
                      : null
                  }
                />
              }
            />
          );
        })}
      </Box>
    </Box>
  );
};
